import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { upperCaseFirst } from 'helpers';
import {
  EstateResultItemServiceStatus,
  EstateResultItemType,
  EstateResultItemVerificationStatus,
  useEstate_GetEstateByIdQuery,
  useEstateResultItem_GetByEstateIdQuery,
  useEstateResultItem_UpdateEstateResultItemMutation,
  useEstateResultItem_UpdateVerificationStatusMutation,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import AddressAsset from 'pages/estate-results-page/components/address-asset';
import BankruptcyAsset from 'pages/estate-results-page/components/bankruptcy-asset';
import CollectionAsset from 'pages/estate-results-page/components/collection-asset';
import EmploymentAsset from 'pages/estate-results-page/components/employment-asset';
import NameAsset from 'pages/estate-results-page/components/name-asset';
import PropertyAsset from 'pages/estate-results-page/components/property-asset';
import SourceAsset from 'pages/estate-results-page/components/source-asset';
import TradeAsset from 'pages/estate-results-page/components/trade-asset';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import AddResultItemButton from './components/add-result-item-button';
import AddResultItemModal from './components/add-result-item-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  listAction: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  nameWrapper: {
    display: 'block',
    lineHeight: '1.43',
  },
  success: {
    color: 'green',
  },
  tabs: { minHeight: '15px' },
  tab: { minHeight: '15px', minWidth: '60px' },
}));

export default function SuperAdminEstateResultItems() {
  const classes = useStyles();
  const { estateId } = useParams<Record<string, string>>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tabSelected, setTabSelected] = useState<number>(0);
  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: {
      id: estateId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const [showModal, setShowModal] = useState(false);
  const handleAddResultItemClick = () => {
    setShowModal(true);
  };

  const getAllEstateResultItemsFilter = () => {
    let type, verified, visibility;
    switch (tabSelected) {
      case 0:
        break;
      case 1:
        type = EstateResultItemType.Address;
        break;
      case 2:
        type = EstateResultItemType.Bankruptcy;
        break;
      case 3:
        type = EstateResultItemType.Collection;
        break;
      case 4:
        type = EstateResultItemType.Employment;
        break;
      case 5:
        type = EstateResultItemType.Name;
        break;
      case 6:
        type = EstateResultItemType.Trade;
        break;
      case 7:
        type = EstateResultItemType.Property;
        break;
      case 8:
        type = EstateResultItemType.Form_1040;
        break;
      case 9:
        type = EstateResultItemType.FormW2;
        break;
      case 10:
        type = EstateResultItemType.Form_1099;
        break;
      case 11:
        type = EstateResultItemType.Source;
        break;
      case 12:
        // TODO verificationStatus
        break;
      case 13:
        // TODO visbility
        break;
      default:
        break;
    }
    return {
      estateId: estateId,
      filter: {
        type: type,
        verificationStatus: verified,
        visibility: visibility,
      },
    };
  };

  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: refetchEstateResultItems,
  } = useEstateResultItem_GetByEstateIdQuery({
    variables: getAllEstateResultItemsFilter(),
    onError: (e) => {
      showAlert({
        message: 'Error getting estate result item by estate ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateVerification] = useEstateResultItem_UpdateVerificationStatusMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating verification status: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const [updateResultItem] = useEstateResultItem_UpdateEstateResultItemMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate result item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionMenuClick = () => {
    setAnchorEl(null);
  };

  const handlePropertyVerificationClick = (itemId: string) => {
    updateVerification({
      variables: {
        input: {
          id: itemId,
          verificationStatus: EstateResultItemVerificationStatus.Verified,
        },
      },
      onCompleted: () => {
        showToast({
          message: 'Result item marked as verified.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleItemVisibilityClick = (item: any, visible: boolean) => {
    if (item.verificationStatus !== EstateResultItemVerificationStatus.Verified) {
      showAlert({
        message: 'Only verified properties can be displayed.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    updateResultItem({
      variables: {
        input: {
          id: item.id,
          type: item.type,
          source: item.source,
          visible: visible,
        },
      },
      onCompleted: () => {
        showToast({
          message: `Result item marked as: ${visible ? 'visible' : 'hidden'}.`,
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMarkCompletedClick = (item: any) => {
    updateResultItem({
      variables: {
        input: {
          id: item.id,
          type: item.type,
          source: item.source,
          serviceStatus: EstateResultItemServiceStatus.Complete,
        },
      },
      onCompleted: () => {
        handleActionMenuClick();
        showToast({
          message: 'Result item marked as completed.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTabChange = (_e: ChangeEvent<any>, newValue: number) => {
    setTabSelected(newValue);
  };

  const typeLabel = (type: EstateResultItemType) => {
    return upperCaseFirst(type);
  };

  const serviceLabel = (type: EstateResultItemType, status: EstateResultItemServiceStatus) => {
    if (type === EstateResultItemType.Source) {
      return 'N/A';
    }
    return status ? upperCaseFirst(status) : 'Unassigned';
  };

  useEffect(() => {
    refetchEstateResultItems(getAllEstateResultItemsFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSelected]);

  if (estateLoading && !estateData && itemsLoading && !itemsData) {
    return <Spinner />;
  }

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'type',
      filterable: false,
      flex: 3,
      headerName: 'Item type',
      renderCell: (params: GridRenderCellParams) => {
        return <div>{typeLabel(params.row.type)}</div>;
      },
      valueGetter: (params: GridValueGetterParams) => params.row.type,
    },
    {
      disableColumnMenu: true,
      field: 'source',
      filterable: false,
      flex: 3,
      headerName: 'Item source',
      renderCell: (params: GridRenderCellParams) => {
        return <div>{params.row.source}</div>;
      },
      valueGetter: (params: GridValueGetterParams) => params.row.source,
    },
    {
      disableColumnMenu: true,
      field: 'data',
      filterable: false,
      sortable: false,
      flex: 10,
      headerName: 'Result item card',
      renderCell: (params: GridRenderCellParams) => {
        const fields = JSON.parse(params.row.essentialData);
        return (
          <>
            <span>
              {params.row.type === EstateResultItemType.Trade ? (
                <TradeAsset
                  id={fields.id}
                  showAssetBtn={true}
                  trade={fields}
                  title={params.row.title}
                  subtitle={params.row.subtitle}
                />
              ) : params.row.type === EstateResultItemType.Address ? (
                <AddressAsset
                  id={fields.id}
                  showAssetBtn={true}
                  address={fields}
                  title={params.row.title}
                  subtitle={params.row.subtitle}
                />
              ) : params.row.type === EstateResultItemType.Collection ? (
                <CollectionAsset
                  id={fields.id}
                  showAssetBtn={true}
                  collection={fields}
                  title={params.row.title}
                  subtitle={params.row.subtitle}
                />
              ) : params.row.type === EstateResultItemType.Employment ? (
                <EmploymentAsset
                  id={fields.id}
                  showAssetBtn={true}
                  employment={fields}
                  title={params.row.title}
                  subtitle={params.row.subtitle}
                />
              ) : params.row.type === EstateResultItemType.Bankruptcy ? (
                <BankruptcyAsset
                  id={fields.id}
                  showAssetBtn={true}
                  bankruptcy={fields}
                  title={params.row.title}
                />
              ) : params.row.type === EstateResultItemType.Name ? (
                <NameAsset
                  id={fields.id}
                  showAssetBtn={true}
                  name={fields}
                  title={params.row.title}
                />
              ) : params.row.type === EstateResultItemType.Property ? (
                <PropertyAsset
                  id={fields.id}
                  showAssetBtn={true}
                  property={fields}
                  title={params.row.title}
                  subtitle={params.row.subtitle}
                />
              ) : (
                <SourceAsset
                  id={fields.id}
                  showAssetBtn={true}
                  source={fields}
                  title={params.row.title}
                />
              )}
            </span>
            <Tooltip
              title={
                <Fragment>
                  {Object.keys(fields).map((key) => {
                    let value = fields[key];
                    if (key === 'activityDesignatorCode') {
                      value = `Description: ${value}`;
                    } else if (key === 'actions') {
                      value = value.join(',');
                    }
                    return (
                      <div key={key}>
                        {key}: {value} <br />
                      </div>
                    );
                  })}
                </Fragment>
              }
            >
              <div>all data</div>
            </Tooltip>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'updated',
      filterable: false,
      flex: 3,
      headerName: 'Updated',
      renderCell: (params: GridRenderCellParams) => {
        const updated = params.row.updatedAt;
        const date: string = format(Date.parse(updated!.toString()), 'LLL d y');
        const time: string = format(Date.parse(updated!.toString()), 'h:mm bbb');
        return (
          <div className={classes.nameWrapper}>
            <div>
              <Typography variant="caption">{date}</Typography>
            </div>
            <div className={classes.nameWrapper}>
              <Typography variant="caption">{time}</Typography>
            </div>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.updatedAt,
    },
    {
      disableColumnMenu: true,
      field: 'verified',
      filterable: false,
      flex: 3,
      headerName: 'Verified',
      renderCell: (params: GridRenderCellParams) => {
        const verified =
          params.row.verificationStatus === EstateResultItemVerificationStatus.Verified;
        if (
          params.row.type === EstateResultItemType.Property ||
          params.row.type === EstateResultItemType.Address
        ) {
          if (
            params.row.verificationStatus === EstateResultItemVerificationStatus.NeedsVerification
          ) {
            return (
              <Button
                onClick={() => {
                  handlePropertyVerificationClick(params.row.id);
                }}
                color="secondary"
                variant="contained"
                size="small"
                disabled={verified}
              >
                mark verified
              </Button>
            );
          } else {
            // No actions exist for Suspected, Duplicate statuses
            let label = undefined;
            switch (params.row.verificationStatus) {
              case EstateResultItemVerificationStatus.Suspected:
                label = 'Suspected';
                break;
              case EstateResultItemVerificationStatus.Duplicate:
                label = 'Duplicate';
                break;
              case EstateResultItemVerificationStatus.Verified:
                label = 'Verified';
                break;
              default:
                label = '';
                break;
            }
            return (
              <Chip className={classes.success} size="small" label={label} variant="outlined" />
            );
          }
        } else {
          return (
            <Chip className={classes.success} size="small" label="Verified" variant="outlined" />
          );
        }
      },
      valueGetter: (params: GridValueGetterParams) => params.row.verificationStatus,
    },
    {
      disableColumnMenu: true,
      field: 'visible',
      filterable: true,
      flex: 3,
      headerName: 'Visible',
      renderCell: (params: GridRenderCellParams) => {
        const visibiliy = params.row.visible;
        return (
          <Button
            onClick={() => {
              handleItemVisibilityClick(params.row, !visibiliy);
            }}
            color={visibiliy ? 'primary' : 'secondary'}
            variant="contained"
            size="small"
          >
            {visibiliy ? 'hide' : 'show'}
          </Button>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.visible,
    },
    {
      disableColumnMenu: true,
      field: 'service',
      filterable: false,
      sortable: false,
      flex: 3,
      headerName: 'Service status',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            {serviceLabel(params.row.type, params.row.serviceStatus)}
            <List style={{ position: 'absolute', right: 0 }}>
              <ListItem>
                <ListItemSecondaryAction className={classes.listAction}>
                  <IconButton
                    id={params.row.id}
                    edge="end"
                    onClick={(e) => {
                      handleActionClick(e);
                    }}
                    aria-controls={params.row.id}
                    size="large"
                  >
                    <MoreVertical />
                  </IconButton>
                  <Menu
                    open={anchorEl?.id === params.row.id}
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={() => handleActionMenuClick()}
                  >
                    <MenuItem
                      key="mark complete"
                      onClick={() => {
                        handleMarkCompletedClick(params.row);
                      }}
                      disabled={params.row.type === EstateResultItemType.Source}
                    >
                      Service status: mark complete
                    </MenuItem>
                  </Menu>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </>
        );
      },
    },
  ];

  const rows = [];
  const items = itemsData ? itemsData.data : [];
  for (const item of items) {
    rows.push(item);
  }

  return (
    <HomeLayout showPortalName={false}>
      <div className={classes.container}>
        <AddResultItemButton onClick={handleAddResultItemClick} />
        <Tabs
          value={tabSelected}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
          className={classes.tabs}
        >
          <Tab className={classes.tab} label="Summary" />
          <Tab className={classes.tab} label="Addresses" />
          <Tab className={classes.tab} label="Bankrupties" />
          <Tab className={classes.tab} label="Collections" />
          <Tab className={classes.tab} label="Employment" />
          <Tab className={classes.tab} label="Names" />
          <Tab className={classes.tab} label="Trades" />
          <Tab className={classes.tab} label="Properties" />
          <Tab className={classes.tab} label="Form 1040" />
          <Tab className={classes.tab} label="Form W2" />
          <Tab className={classes.tab} label="Form 1099" />
          <Tab className={classes.tab} label="Sources" />
        </Tabs>

        <DataGrid
          className={classes.root}
          columns={columns}
          rows={rows}
          disableColumnReorder
          disableRowSelectionOnClick
          autoHeight={true}
          rowHeight={80}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
        />
        <AddResultItemModal
          showModal={showModal}
          setShowModal={setShowModal}
          onExitClick={() => setShowModal(false)}
        />
      </div>
    </HomeLayout>
  );
}
